import { dsText, fontSize, tokenVar } from '../../utils'

export type ScheduleStatusTone = 'open' | 'closed' | 'holiday' | 'override'

export interface ScheduleStatusBadgeProps {
  label: string
  tone?: ScheduleStatusTone
  size?: 'sm' | 'md'
}

const WarningIcon = ({ color }: { color: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 32 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.8944 1.78885L30.2764 28.5528C30.6088 29.2177 30.1253 30 29.382 30H2.61803C1.87465 30 1.39116 29.2177 1.72361 28.5528L15.1056 1.78885C15.4741 1.05181 16.5259 1.0518 16.8944 1.78885Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
    <path
      d="M16 10L16 20"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
    <circle cx="16" cy="25" r="1" stroke={color} strokeWidth="2" />
  </svg>
)

const getToneStyles = (tone: ScheduleStatusTone) => {
  if (tone === 'override') {
    return {
      bg: tokenVar('orange.subtle'),
      fg: tokenVar('orange.fg'),
      indicatorColor: tokenVar('orange.fg'),
      indicatorType: 'icon' as const,
      borderRadius: '8px',
      icon: <WarningIcon color={tokenVar('orange.fg')} />,
    }
  }

  if (tone === 'open') {
    return {
      bg: tokenVar('green.subtle'),
      fg: tokenVar('green.fg'),
      indicatorColor: tokenVar('green.solid'),
      indicatorType: 'dot' as const,
      borderRadius: '6px',
      icon: undefined,
    }
  }

  if (tone === 'holiday') {
    return {
      bg: tokenVar('blue.subtle'),
      fg: tokenVar('blue.fg'),
      indicatorColor: tokenVar('blue.solid'),
      indicatorType: 'dot' as const,
      borderRadius: '6px',
      icon: undefined,
    }
  }

  return {
    bg: tokenVar('red.subtle'),
    fg: tokenVar('red.fg'),
    indicatorColor: tokenVar('red.solid'),
    indicatorType: 'dot' as const,
    borderRadius: '6px',
    icon: undefined,
  }
}

export const ScheduleStatusBadge = ({
  label,
  tone = 'closed',
  size = 'sm',
}: ScheduleStatusBadgeProps) => {
  const toneStyles = getToneStyles(tone)
  const isMedium = size === 'md'

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isMedium ? '10px' : '8px',
        padding: isMedium ? '8px 14px' : '4px 10px',
        borderRadius: isMedium ? '8px' : toneStyles.borderRadius,
        backgroundColor: toneStyles.bg,
        color: toneStyles.fg,
      }}
    >
      {toneStyles.indicatorType === 'icon' ? (
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: toneStyles.indicatorColor,
            flexShrink: 0,
          }}
        >
          {toneStyles.icon}
        </span>
      ) : (
        <span
          aria-hidden
          style={{
            width: isMedium ? '14px' : '8px',
            height: isMedium ? '14px' : '8px',
            borderRadius: '999px',
            backgroundColor: toneStyles.indicatorColor,
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          fontFamily: isMedium ? dsText.H6.fontFamily : dsText.Body2.fontFamily,
          fontSize: fontSize(
            isMedium ? dsText.H6.fontSize : dsText.Body2.fontSize,
          ),
          fontWeight: isMedium
            ? dsText.H6.fontWeight
            : dsText.Body2_Bold.fontWeight,
          lineHeight: isMedium ? dsText.H6.lineHeight : dsText.Body2.lineHeight,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </span>
  )
}
